import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react';
import {
    FilterBarContainer,
    FilterBarLeftContent,
    FilterBarRightContent,
    SearchContainer,
} from "./Filters.styled";
import { Button, TextField, SortMenu, ButtonMenu } from "@ann/web-components";
import { useDrivesStores } from '../../../../hook/use-drives.hook';
import { useStore } from '../../../../../../../hooks';
import { documentsData } from '../../../../mockData';

const Filters = ({ 
    mode, 
    setMode, 
    onFilterChange, 
    onSortChange, 
    sortBy, 
    sortDirection,
    filterText = ""
}) => {
    const { sharedWithMeStore } = useDrivesStores();
    const { authStore } = useStore();
    const [selectedOwner, setSelectedOwner] = useState(null);
    const [searchValue, setSearchValue] = useState(filterText);
    const [filtersKey, setFiltersKey] = useState(0);
    const searchTimeoutRef = useRef(null);


    // Load users when component mounts
    useEffect(() => {
        const loadUsers = async () => {
            if (!authStore.currentWorkspaceUuid) return;
            if (authStore.users?.length > 0) return; // Already loaded
            
            try {
                await authStore.fetchAllUsers();
            } catch (error) {
                console.error('Error loading users:', error);
            }
        };
        loadUsers();
    }, [authStore.currentWorkspaceUuid, authStore.users]);

    const ownerOptions = useMemo(() => {
        if (documentsData.length > 0) 
            return documentsData.map(document => ({
            value: document.id,
            label: document.owner_full_name
        })).sort((a, b) => a.label.localeCompare(b.label));
    }, [documentsData]);


    const handleSelectionOwner = (value) => {
        // ButtonMenu onChange receives the value of selected item
        // If same value is selected, clear the filter (toggle off)
        const currentValue = selectedOwner?.value || selectedOwner;
        const newOwner = currentValue === value ? null : value;
        
        // Find the full owner object based on the value
        const selectedOwnerObj = newOwner 
            ? ownerOptions.find(owner => owner.value === newOwner)
            : null;
        
        setSelectedOwner(selectedOwnerObj);
        
        // Update store filter
        if (sharedWithMeStore.setSelectedOwner) {
            sharedWithMeStore.setSelectedOwner(newOwner ? [newOwner] : null);
        }
    };

    // Sync local state with prop when it changes
    useEffect(() => {
        setSearchValue(filterText);
    }, [filterText]);

    const handleSearchChange = useCallback((value) => {
        setSearchValue(value);
        
        // Clear existing timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        
        // Set new timeout for 500ms debounce
        searchTimeoutRef.current = setTimeout(() => {
            if (onFilterChange) {
                onFilterChange(value);
            }
        }, 500);
    }, [onFilterChange]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    const handleClearAllFilters = () => {
        setSelectedOwner(null);
        setSearchValue("");
        
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        
        setFiltersKey((prev) => prev + 1);
        if (onFilterChange) {
            onFilterChange("");
        }
        
        // Clear owner filter in store
        if (sharedWithMeStore.setSelectedOwner) {
            sharedWithMeStore.setSelectedOwner(null);
        }
    };

    // Check if there are active filters
    const hasActiveFilters = searchValue.trim() !== "" || selectedOwner !== null;

    return (
        <FilterBarContainer>
            <FilterBarLeftContent id="filterWrapper">
                <SearchContainer>
                    <TextField
                        iconLeft="search"
                        onChange={handleSearchChange}
                        size="small"
                        placeholder="Search"
                        value={searchValue}
                    />
                </SearchContainer>
                <ButtonMenu
                    key={`owner-filter-${filtersKey}`}
                    iconPosition="right"
                    label={selectedOwner?.label || "Owner"}
                    menuPosition="bottom"
                    onChange={(value) => {
                        handleSelectionOwner(value);
                    }}
                    size="medium"
                    type="secondary"
                    width="auto"
                    usePortal={true}
                >
                    <ButtonMenu.Menu>
                        {ownerOptions.map((owner) => (
                            <ButtonMenu.Item
                                key={owner.value}
                                label={owner.label || 'Owner'}
                                value={owner.value}
                            >
                                {owner.label}
                            </ButtonMenu.Item>
                        ))}
                    </ButtonMenu.Menu>
                </ButtonMenu>

                {hasActiveFilters && (
                    <Button
                        icon=""
                        iconLeft=""
                        iconRight=""
                        label="Clear Filters"
                        size="medium"
                        type="secondary"
                        onClick={handleClearAllFilters}
                    />
                )}
            </FilterBarLeftContent>

            <SortMenu
                minWidth="200px"
                onChange={(id) => {
                    if (onSortChange) {
                        onSortChange(id);
                    }
                }}
                onOrderChange={() => {
                    if (onSortChange) {
                        onSortChange(sortBy);
                    }
                }}
                options={[
                    {
                        icon: 'clock',
                        id: 'lastUpdated',
                        label: 'Most Recent'
                    },
                    {
                        icon: 'sortByAlpha',
                        id: 'name',
                        label: 'Name (A-Z)'
                    }
                ]}
                order={sortDirection}
                selected={sortBy}
                size="medium"
                title="SORT BY"
                width="200px"
            />

            <FilterBarRightContent>
                
            </FilterBarRightContent>
        </FilterBarContainer>
    );
};

export default observer(Filters);
